/**
 * Turning what a dispatcher typed into somewhere a car can be sent.
 *
 * Separate from the field that uses it so the component file exports only a component —
 * fast refresh stops working for a module that mixes the two, and losing it on the screen
 * being iterated on most is a poor trade for one fewer file.
 */

import type { Schema } from '@taxi/api-client';

import { canonicalEntrance } from '../places/entrances';
import {
  customersApi,
  geoApi,
  type AddressSuggestion,
  type DrawnLevels,
  type GeocodeResult,
  type SearchMode,
  type SearchScope,
  type StreetRef,
} from './api';

/** What makes a search hit one of the firm's own addresses (#420). */
type FirmPlaceHit = Schema<'FirmPlaceHit'>;

/** A place the dispatcher can pick, from either the caller's history or a map search. */
export interface Place {
  address: string;
  longitude: number;
  latitude: number;
  /** Set when this came from the caller's own history, which is worth showing. */
  label?: string | null;
  useCount?: number;
  /** The level the automatic search had to widen to, when that was not the town. */
  foundIn?: SearchScope;

  /**
   * One of the firm's own addresses (#420), or an entrance typed that nobody has placed yet
   * (an id of null). What the list marks, what folds a building's doors together, and the
   * note the dispatcher reads under the address.
   */
  firm?: FirmPlaceHit | null;

  /**
   * The firm's place this is, for the order: a search hit's own, or the place a caller's
   * saved address reads the same as. The server fills the point, the note and the door
   * from it.
   */
  placeId?: string | null;

  /** Set when the map found a street itself rather than a place on it: how its numbers are asked for. */
  street?: StreetRef | null;

  /** The house number alone, on a row that is one of a street's numbers — what typing narrows by. */
  number?: string;
}

/**
 * What a saved address needs to become a place. The customer's list carries its use count
 * as well; the one that arrives with a ringing call does not, and is a place all the same.
 */
type SavedAddress = Pick<AddressSuggestion, 'address' | 'longitude' | 'latitude' | 'label'> &
  Partial<Pick<AddressSuggestion, 'useCount' | 'placeId'>>;

export function toPlace(address: SavedAddress): Place | null {
  // An address never placed on a map — an imported one, usually — is not a place yet.
  // Offered as one, it went into the order with no coordinates and was refused on save;
  // typed, it is found by the map search like any other text.
  if (address.longitude === null || address.latitude === null) return null;

  return {
    address: address.address,
    longitude: address.longitude,
    latitude: address.latitude,
    label: address.label,
    useCount: address.useCount,
    placeId: address.placeId ?? null,
  };
}

/** The saved addresses that are places, in their order. */
export function placesOf(addresses: readonly SavedAddress[]): Place[] {
  return addresses.flatMap((address) => toPlace(address) ?? []);
}

/**
 * Turns free text into a place.
 *
 * Used when the dispatcher typed an address and never opened the list — which is the
 * normal way to enter somewhere new, not a mistake to be corrected. The caller's own
 * addresses are tried first, so a regular's usual doorway wins over a map hit that merely
 * reads similar.
 */
export async function resolve(
  text: string,
  customerId: string | null,
  mode?: SearchMode,
): Promise<Place | null> {
  const query = text.trim();

  if (query.length === 0) return null;

  if (customerId !== null) {
    const own = placesOf(await customersApi.searchAddresses(customerId, query).catch(() => []));
    if (own.length > 0) return own[0]!;
  }

  const found = await searchMap(query, mode).catch(() => null);

  return found?.places[0] ?? null;
}

/** What a map search found, as places, and whether our map was missing from it. */
export interface MapSearch {
  places: Place[];
  /**
   * The map search was not answering: the places are the firm's own alone, and an empty list
   * is not an address that does not exist.
   */
  mapUnavailable: boolean;
}

/**
 * A map search at a position of the switch, as places. The automatic position marks what it
 * found outside town with the level, so a village never passes for a street in town.
 *
 * With `numbers`, a street found alone comes with its first numbers under it, in order —
 * the *in the list* way of offering a street's numbers.
 */
export async function searchMap(query: string, mode?: SearchMode, numbers = false): Promise<MapSearch> {
  const asPlace = (result: GeocodeResult, foundIn?: SearchScope): Place => ({
    address: result.address,
    longitude: result.location.longitude,
    latitude: result.location.latitude,
    foundIn,
    firm: result.firmPlace ?? null,
    placeId: result.firmPlace?.id ?? null,
    street: result.street ?? null,
  });

  if (mode === 'auto') {
    const { level, results, mapUnavailable } = await geoApi.searchWidening(query, numbers);

    return {
      places: results.map((result) => asPlace(result, level !== null && level !== 'town' ? level : undefined)),
      mapUnavailable,
    };
  }

  const { results, mapUnavailable } = await geoApi.search(query, mode, numbers);

  return { places: results.map((result) => asPlace(result)), mapUnavailable };
}

/** A street picked in the *after the street* way, and every number on it. */
export interface StreetListing {
  /** The street itself, which stays a choice: a caller who gives no number. */
  street: Place;
  /** Its name as the box shows it while the number is typed after it — `Росица `. */
  name: string;
  numbers: Place[];
}

/**
 * Every number on a street a search found, as places — asked at the level the street was
 * found at, which on the automatic position is wherever it had to widen to.
 */
export async function numbersOf(street: Place, mode?: SearchMode): Promise<StreetListing | null> {
  if (street.street == null) return null;

  const scope: SearchScope | undefined = mode === 'auto' ? (street.foundIn ?? 'town') : mode;
  const numbers = await geoApi.streetNumbers(street.street, street, scope);

  return {
    street,
    name: street.street.name,
    numbers: numbers.map((number) => ({
      address: number.address,
      longitude: number.location.longitude,
      latitude: number.location.latitude,
      foundIn: street.foundIn,
      number: number.number,
    })),
  };
}

/** A house number as typing compares it: upper case, and a Latin letter read as its Cyrillic one. */
function foldNumber(text: string): string {
  return [...text.replace(/\s+/g, '')].map((ch) => (/\p{L}/u.test(ch) ? (canonicalEntrance(ch) ?? ch) : ch)).join('');
}

/**
 * What the list shows while a picked street's number is typed after its name: the street and
 * all its numbers before a digit is typed, then the numbers that start with what was typed —
 * `Росица 1` keeps 1, 10, 10А, 11. Null once the text has left the street, or names a number
 * the map does not have, and the box searches as it always does.
 */
export function onStreet(text: string, listing: StreetListing): Place[] | null {
  const typed = text.replace(/\s+/g, ' ').trimStart();
  const name = listing.name.trim();

  if (typed.slice(0, name.length).toLocaleLowerCase('bg') !== name.toLocaleLowerCase('bg')) return null;

  const after = typed.slice(name.length);

  // "Росицата" is another word, not Росица and a number.
  if (after.length > 0 && !/^[\s,]/.test(after)) return null;

  const wanted = foldNumber(after.replace(/^[\s,]+/, ''));

  if (wanted.length === 0) return [listing.street, ...listing.numbers];

  const found = listing.numbers.filter((place) => foldNumber(place.number ?? '').startsWith(wanted));

  return found.length > 0 ? found : null;
}

/**
 * The switch's positions for a firm, narrowest first and automatic last — none at all until
 * the town is drawn, because positions that all search everywhere would be a control that
 * lies. Nearby is left out rather than greyed when it is not drawn; wider is always there,
 * since undrawn it means anywhere.
 */
export function scopePositions(levels: DrawnLevels | null | undefined): SearchMode[] {
  if (levels == null || !levels.town) return [];

  return levels.nearby ? ['town', 'nearby', 'wider', 'auto'] : ['town', 'wider', 'auto'];
}

/** The switch's word for a position: the widest says "anywhere" when it was never drawn. */
export function scopeName(
  t: (key: string) => string,
  position: SearchMode,
  levels: DrawnLevels | null | undefined,
): string {
  return position === 'wider' && levels?.wider !== true ? t('order.scope.anywhere') : t(`order.scope.${position}`);
}

/** How long the desk trusts what it was told about the drawn levels. */
const LEVELS_FRESH_MS = 60_000;

let levelsAsked: { at: number; answer: Promise<DrawnLevels | null> } | null = null;

/**
 * Which levels the firm drew, asked once a minute rather than once per order form.
 *
 * The form is remounted for every call the desk takes, and the answer changes a few times a
 * year. A failure is no levels — the search then runs unbounded, as it did before there were
 * any — and is not remembered, so the next form asks again.
 */
export function drawnLevels(): Promise<DrawnLevels | null> {
  const now = Date.now();

  if (levelsAsked === null || now - levelsAsked.at > LEVELS_FRESH_MS) {
    const answer = geoApi.levels().catch(() => {
      levelsAsked = null;
      return null;
    });

    levelsAsked = { at: now, answer };
  }

  return levelsAsked.answer;
}
